import { onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { clearToken, getStoredToken, hasRequiredScope, hasValidToken } from '@/services/auth'

// '/design-system' is the internal brand book — intentionally auth-free so it
// can be opened without a session (see its route in router/index.ts).
const PUBLIC_PATHS = new Set(['/login', '/design-system'])

export const useAuthWatcher = () => {
  const route = useRoute()
  const router = useRouter()

  let validationInterval: number | undefined

  const isPublicRoute = (path: string) => {
    return PUBLIC_PATHS.has(path) || path.startsWith('/auth/')
  }

  const ensureValidSession = async () => {
    if (isPublicRoute(route.path)) {
      return
    }

    const token = getStoredToken()

    if (!token) {
      await router.replace({ path: '/login', query: { redirect: route.fullPath, reason: 'missing_token' } })
      return
    }

    if (!hasValidToken()) {
      clearToken()
      await router.replace({ path: '/login', query: { redirect: route.fullPath, reason: 'token_expired' } })
      return
    }

    if (!hasRequiredScope('broker')) {
      clearToken()
      await router.replace({ path: '/login', query: { redirect: route.fullPath, reason: 'invalid_scope' } })
    }
  }

  const handleStorage = () => {
    void ensureValidSession()
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void ensureValidSession()
    }
  }

  onMounted(() => {
    window.addEventListener('storage', handleStorage)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    validationInterval = window.setInterval(() => {
      void ensureValidSession()
    }, 5000)

    void ensureValidSession()
  })

  onUnmounted(() => {
    window.removeEventListener('storage', handleStorage)
    document.removeEventListener('visibilitychange', handleVisibilityChange)

    if (validationInterval) {
      clearInterval(validationInterval)
    }
  })
}
